import { Tooltip } from '@mui/material'
import type { GridPreProcessEditCellProps } from '@mui/x-data-grid'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import AppContext from '../../../context/appContext'
import { refDataQueryOptions } from '../../../hooks/data/useRefData'
import { loadMonitoringActivities } from '../../../services/farmMapping/monitoring/monitoringActivities/loadMonitoringActivities'
import { loadMonitoringTasks } from '../../../services/farmMapping/monitoring/monitoringTasks/loadMonitoringTasks'
import { MonitoringActivity } from '../../../types/apiModels'
import {
  PROGRAM_IDS,
  monitoringTaskStatusTypes,
  monitoringWorkflowsTypes,
} from '../../../utils/constants'
import { calculateDAP, DAP_FORMULAS } from '../dapUtils'
import {
  renderEditSingleSelect,
  renderInput,
  renderSelect,
  renderText,
  validateCarbonReplicaRequired,
  validateInputStringRequired,
  validatePercentageNumber,
  validatePositiveNumberRequired,
  validateSingleSelectRequired,
} from '../DataGrid/DataGridControls'
import {
  resolveDoubleDepthSampleVolume,
  resolveScalarSampleVolume,
  type SocGridSamplingPayload,
} from './sampleVolumeFromTasks'

export const monitoringSOCColumn = {
  field: 'monitoringSOCActivityId',
  headerName: 'Actividad SOC',
  headerAlign: 'center',
  validate: ({ value }: { value: unknown }) => validateSingleSelectRequired(value, 'Requerida'),
  renderCell: params => renderSelect({ params, props: { tooltip: { placement: 'top' } } }),
  renderEditCell: params =>
    renderEditSingleSelect({ params, props: { tooltip: { placement: 'top' } } }),
  required: true,
  editable: true,
  sortable: true,
  width: 220,
  type: 'singleSelect',
}

export const dapMethodColumn = {
  field: 'dapMethodId',
  headerName: 'Método DAP',
  headerAlign: 'center',
  validate: ({ value }: { value: unknown }) => validateSingleSelectRequired(value, 'Requerido'),
  renderCell: params => renderSelect({ params, props: { tooltip: { placement: 'top' } } }),
  renderEditCell: params =>
    renderEditSingleSelect({ params, props: { tooltip: { placement: 'top' } } }),
  required: true,
  editable: true,
  sortable: true,
  width: 220,
  type: 'singleSelect',
}

export const monitoringSOCSitesFixedColumns = [
  {
    field: 'name',
    headerName: 'Nombre muestra',
    headerAlign: 'center',
    validate: ({ value }: { value: unknown }) => validateInputStringRequired(value, 'Requerido'),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: true,
    width: 180,
  },
  {
    field: 'sampleVolume',
    headerName: 'Volumen muestra [cm3]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) =>
      validatePositiveNumberRequired({ value, allowZero: false, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'wbFirstReplicaPercentage',
    headerName: 'Rep 1\nWB [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, row, column }) => validateCarbonReplicaRequired({ value, row, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'wbSecondReplicaPercentage',
    headerName: 'Rep 2\nWB [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'caFirstReplicaPercentage',
    headerName: 'Rep 1\nCA [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'caSecondReplicaPercentage',
    headerName: 'Rep 2\nCA [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'lecoFirstReplicaPercentage',
    headerName: 'LECO 1\nCA [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    width: 100,
    editable: true,
    sortable: false,
    align: 'left',
  },
  {
    field: 'lecoSecondReplicaPercentage',
    headerName: 'LECO 2\nCA [%]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) => validatePercentageNumber({ value, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: false,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'dryWeight',
    headerName: 'Peso seco [g]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, column }) =>
      validatePositiveNumberRequired({ value, allowZero: false, column }),
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'gravelWeight',
    headerName: 'Peso grava [g]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, row, column }) => {
      const positiveNumberError = validatePositiveNumberRequired({ value, allowZero: true, column })
      if (positiveNumberError) return positiveNumberError

      if (value && row.dryWeight && parseFloat(value) > parseFloat(row.dryWeight)) {
        return 'El peso de grava no puede ser mayor al peso seco'
      }

      return null
    },
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
  {
    field: 'gravelVolume',
    headerName: 'Volumen grava [cm3]',
    headerAlign: 'center',
    type: 'number',
    validate: ({ value, row, column }) => {
      const positiveNumberError = validatePositiveNumberRequired({ value, allowZero: true, column })
      if (positiveNumberError) return positiveNumberError

      if (value && row.sampleVolume && parseFloat(value) > parseFloat(row.sampleVolume)) {
        return 'El volumen de grava no puede ser mayor al volumen de muestra'
      }

      return null
    },
    renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
    renderEditCell: params => renderInput({ params, props: { tooltip: { placement: 'bottom' } } }),
    required: true,
    editable: true,
    sortable: false,
    width: 100,
    align: 'left',
  },
]

// Read-only depth columns shown only for ReTerra double-depth rows.
export const initialDepthColumn = {
  field: 'initialDepth',
  headerName: 'Prof. inicial [cm]',
  headerAlign: 'center',
  type: 'number',
  editable: false,
  sortable: false,
  width: 110,
  align: 'left',
}

export const finalDepthColumn = {
  field: 'finalDepth',
  headerName: 'Prof. final [cm]',
  headerAlign: 'center',
  type: 'number',
  editable: false,
  sortable: false,
  width: 110,
  align: 'left',
}

export const dapColumn = {
  field: 'dap',
  headerName: 'DAP [g/cm3]',
  headerAlign: 'center',
  type: 'number',
  validate: ({ value, column }) =>
    validatePositiveNumberRequired({ value, allowZero: true, column }),
  renderCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
  renderEditCell: params => renderText({ params, props: { tooltip: { placement: 'bottom' } } }),
  required: true,
  editable: false,
  sortable: false,
  width: 100,
  align: 'left',
}

type ValueOption = { value: string | number; label: string }

type SOCSitesColumn = {
  field: string
  valueOptions?: ValueOption[] | ((params: { id: string | number }) => ValueOption[])
  [key: string]: unknown
}

type SOCSampleRow = {
  id: string | number
  monitoringSOCActivityId?: string | null
  initialDepth?: number | null
  finalDepth?: number | null
  isDeleted?: boolean
  errors?: Record<string, string>
  [key: string]: unknown
}

type DepthEntry = {
  initialDepth: number | null
  finalDepth: number | null
  sampleVolume: number | null
}

// Composite key for depth-keyed pre-load map: activityId|initialDepth|finalDepth
const buildDepthKey = (
  activityId: string,
  initialDepth: number | null,
  finalDepth: number | null,
): string => `${activityId}|${initialDepth ?? 'null'}|${finalDepth ?? 'null'}`

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === ''

const readTaskPayload = (task: { dataPayload?: unknown }): Record<string, unknown> | null =>
  task.dataPayload && typeof task.dataPayload === 'object'
    ? (task.dataPayload as Record<string, unknown>)
    : null

export const useSOCSitesColumns = (farmId: string) => {
  const { programConfig } = useContext(AppContext)
  const queryClient = useQueryClient()
  const [monitoringSOCSitesColumns, setMonitoringSOCSitesColumns] = useState<
    SOCSitesColumn[] | null
  >(null)
  const [monitoringSOCSitesSamples, setMonitoringSOCSitesSamples] = useState<SOCSampleRow[]>([])
  const [completedSOCActivities, setCompletedSOCActivities] = useState<MonitoringActivity[]>([])
  // Depth-keyed rows pre-computed from double-depth task payload, used for new-sample seeding
  // (ReTerra, always; SOC-V2, when the program includes double-depth activities).
  const initialSiteRowsRef = useRef<SOCSampleRow[]>([])
  // Activity ids that use double depth for the currently loaded activity set. ReTerra activities
  // are always double-depth (the fetch already narrows the query to SOCRETERRA); in mixed
  // (non-ReTerra) programs this holds only the SOC-V2 activity ids, since SOC/POA activities in
  // the same program are scalar. Read by the columns memo and getInitialSiteRows below.
  const doubleDepthActivityIdsRef = useRef<Set<string>>(new Set())
  // Resolved sample volumes from the MDC task payload, keyed by activity id (scalar) or by
  // activity+depth pair (double depth). Read on row commit, after loadColumns has filled it.
  const tasksDataMapRef = useRef<Record<string, number | null>>({})

  const resolveTaskSampleVolume = useCallback((row: SOCSampleRow): number | null => {
    const activityId = row?.monitoringSOCActivityId
    if (typeof activityId !== 'string') return null

    const key = doubleDepthActivityIdsRef.current.has(activityId)
      ? buildDepthKey(
          activityId,
          (row.initialDepth ?? null) as number | null,
          (row.finalDepth ?? null) as number | null,
        )
      : activityId

    return tasksDataMapRef.current[key] ?? null
  }, [])

  const hasTaskLookupChanged = useCallback(
    (row: SOCSampleRow, oldRow: SOCSampleRow | undefined): boolean => {
      const activityId = row?.monitoringSOCActivityId
      if (activityId !== oldRow?.monitoringSOCActivityId) return true

      if (typeof activityId === 'string' && doubleDepthActivityIdsRef.current.has(activityId)) {
        return (
          (row.initialDepth ?? null) !== (oldRow?.initialDepth ?? null) ||
          (row.finalDepth ?? null) !== (oldRow?.finalDepth ?? null)
        )
      }

      return false
    },
    [],
  )

  const loadColumns = useCallback(async () => {
    const isReTerra = programConfig?.programId === PROGRAM_IDS.RETERRA

    const monitoringActivityData = await loadMonitoringActivities({
      farmId,
      taskStatusId: [monitoringTaskStatusTypes.Finished, monitoringTaskStatusTypes.InProgress],
      monitoringWorkflowId: isReTerra
        ? [monitoringWorkflowsTypes.SOCRETERRA]
        : [
            monitoringWorkflowsTypes.SOC,
            monitoringWorkflowsTypes.SOCPOA,
            monitoringWorkflowsTypes.SOCV2,
          ],
      sort: 'key:asc',
      size: 1000,
    })
    const activities = monitoringActivityData.items
    setCompletedSOCActivities(activities)
    const dapMethods = await queryClient.ensureQueryData(refDataQueryOptions('RefDAPMethod'))

    // Per-activity "uses double depth", not per-program: the ReTerra query above already
    // narrows to SOCRETERRA-only, so every activity fetched in that branch is double-depth.
    // The non-ReTerra branch fetches SOC/SOCPOA/SOCV2 together (they coexist in the same
    // program), so double depth must be derived per activity from its own workflow id —
    // only SOCV2 activities are double-depth there; SOC/SOCPOA stay scalar.
    const doubleDepthActivityIds = isReTerra
      ? activities.map(activity => activity.id)
      : activities
          .filter(
            activity =>
              typeof activity.monitoringWorkflowId === 'number' &&
              activity.monitoringWorkflowId === monitoringWorkflowsTypes.SOCV2,
          )
          .map(activity => activity.id)
    const doubleDepthActivityIdSet = new Set(doubleDepthActivityIds)
    doubleDepthActivityIdsRef.current = doubleDepthActivityIdSet
    const scalarActivityIds = activities
      .map(activity => activity.id)
      .filter(id => !doubleDepthActivityIdSet.has(id))

    const tasksDataMap: Record<string, number | null> = {}
    const depthRows: SOCSampleRow[] = []

    // SocGridSamplingDoubleDepth defines which depth pairs exist per activity.
    // DapSampleDoubleDepth carries sampleVolume for each depth pair (matched by key).
    // Both canonicalizers emit flat dataPayload — no wrapper key.
    await Promise.all([
      doubleDepthActivityIds.length > 0
        ? Promise.all([
            loadMonitoringTasks({
              monitoringActivityId: doubleDepthActivityIds,
              formName: 'SocGridSamplingDoubleDepth',
              size: 1000,
            }),
            loadMonitoringTasks({
              monitoringActivityId: doubleDepthActivityIds,
              formName: 'DapSampleDoubleDepth',
              size: 1000,
            }),
          ]).then(([socGridTasks, dapTasks]) => {
            const dapVolumeByKey: Record<string, number | null> = {}
            dapTasks?.items?.forEach(task => {
              const payload = task.dataPayload as { dapSamples?: DepthEntry[] }
              if (!task.monitoringActivityId || !Array.isArray(payload?.dapSamples)) return
              payload.dapSamples!.forEach(entry => {
                const key = buildDepthKey(
                  task.monitoringActivityId!,
                  entry.initialDepth,
                  entry.finalDepth,
                )
                dapVolumeByKey[key] = entry.sampleVolume
              })
            })

            // One activity spawns four grid tasks (stakes P1–P4) repeating the same depth
            // pairs; seed one row per (activity, depth pair) with the key as id, or the
            // grid receives duplicate row ids.
            const seededDepthKeys = new Set<string>()
            socGridTasks?.items?.forEach(task => {
              const payload = task.dataPayload as SocGridSamplingPayload & {
                socSamples?: Pick<DepthEntry, 'initialDepth' | 'finalDepth'>[]
              }
              if (!task.monitoringActivityId || !Array.isArray(payload?.socSamples)) return
              payload.socSamples!.forEach(entry => {
                const key = buildDepthKey(
                  task.monitoringActivityId!,
                  entry.initialDepth,
                  entry.finalDepth,
                )
                // Disturbance and diameter are captured once per task, not per depth row.
                const sampleVolume = resolveDoubleDepthSampleVolume({
                  explicitSampleVolume: dapVolumeByKey[key],
                  gridSamplingPayload: payload,
                  initialDepth: entry.initialDepth,
                  finalDepth: entry.finalDepth,
                })
                tasksDataMap[key] = sampleVolume
                if (seededDepthKeys.has(key)) return
                seededDepthKeys.add(key)
                depthRows.push({
                  id: `initial-${key}`,
                  isNew: true,
                  monitoringSOCActivityId: task.monitoringActivityId,
                  initialDepth: entry.initialDepth,
                  finalDepth: entry.finalDepth,
                  sampleVolume,
                })
              })
            })
          })
        : Promise.resolve(),
      scalarActivityIds.length > 0
        ? Promise.all([
            loadMonitoringTasks({
              monitoringActivityId: scalarActivityIds,
              formName: 'DapSample',
              size: 1000,
            }),
            // Enhancement source only: it feeds the corer fallback for activities with no
            // recorded volume. Losing it must not cost the recorded DapSample volumes nor keep
            // the dialog from opening, unlike the other three loads.
            loadMonitoringTasks({
              monitoringActivityId: scalarActivityIds,
              formName: 'SocGridSampling',
              size: 1000,
            }).catch(() => ({ items: [] })),
          ]).then(([dapTasks, socGridTasks]) => {
            const explicitVolumeByActivity: Record<string, unknown> = {}
            dapTasks?.items?.forEach(task => {
              const payload = readTaskPayload(task)
              if (!task.monitoringActivityId || !payload) return
              if ('sampleVolume' in payload) {
                explicitVolumeByActivity[task.monitoringActivityId] = payload.sampleVolume
              }
            })

            // Four grid-point tasks (P1-P4) per activity; the corer formula averages them.
            const gridPayloadsByActivity: Record<string, SocGridSamplingPayload[]> = {}
            socGridTasks?.items?.forEach(task => {
              const payload = readTaskPayload(task)
              if (!task.monitoringActivityId || !payload) return
              const activityPayloads = gridPayloadsByActivity[task.monitoringActivityId] ?? []
              activityPayloads.push(payload as SocGridSamplingPayload)
              gridPayloadsByActivity[task.monitoringActivityId] = activityPayloads
            })

            scalarActivityIds.forEach(activityId => {
              tasksDataMap[activityId] = resolveScalarSampleVolume({
                explicitSampleVolume: explicitVolumeByActivity[activityId],
                gridSamplingPayloads: gridPayloadsByActivity[activityId] ?? [],
              })
            })
          })
        : Promise.resolve(),
    ])
    initialSiteRowsRef.current = depthRows
    tasksDataMapRef.current = tasksDataMap

    const updatedMonitoringSOCSitesColumns = monitoringSOCSitesFixedColumns.map(column => {
      if (column.field === 'sampleVolume') {
        return {
          ...column,
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const monitoringActivityId =
              params.otherFieldsProps?.monitoringSOCActivityId?.value ??
              params.row?.monitoringSOCActivityId

            const taskSampleVolume = resolveTaskSampleVolume({
              ...params.row,
              monitoringSOCActivityId: monitoringActivityId,
            } as SOCSampleRow)

            const currentValue = params.props?.value
            return {
              ...params.props,
              value: currentValue ?? taskSampleVolume,
            }
          },
        }
      }
      if (column.field === 'gravelVolume') {
        return {
          ...column,
          required: DAP_FORMULAS[programConfig?.programId]?.gravelVolumeRequired,
        }
      }
      return column
    })

    const columns: SOCSitesColumn[] = [
      {
        ...monitoringSOCColumn,
        valueOptions: activities.map(completedSOCActivity => ({
          value: completedSOCActivity.id,
          label: completedSOCActivity.key,
        })),
      },
      {
        ...dapMethodColumn,
        valueOptions: dapMethods.map(dapMethod => ({
          value: dapMethod.id,
          label: dapMethod.es_AR || '',
        })),
      },
      ...updatedMonitoringSOCSitesColumns,
      // Shown whenever the loaded activity set includes any double-depth activity (ReTerra —
      // always — or SOC-V2 mixed into a SARA/POA program). Hidden when a program's activities
      // are all scalar, since the columns would otherwise be entirely empty.
      ...(doubleDepthActivityIds.length > 0 ? [initialDepthColumn, finalDepthColumn] : []),
      {
        ...dapColumn,
        renderHeader: () => (
          <Tooltip placement="top" title={DAP_FORMULAS[programConfig?.programId]?.label}>
            <span>DAP [g/cm3]</span>
          </Tooltip>
        ),
      },
    ]

    setMonitoringSOCSitesColumns(columns)
    return columns
  }, [farmId, programConfig?.programId, queryClient, resolveTaskSampleVolume])

  const processRowUpdate = useCallback(
    (newRow: SOCSampleRow) => {
      const { errors, ...updateRowWithoutErrors } = newRow
      const oldRow = monitoringSOCSitesSamples.find(row => row.id === newRow.id)

      // Pre-filled here rather than only in the cell's preProcessEditCellProps, so the MDC volume
      // is readable in the row as soon as its activity is committed, without opening the cell.
      // Only on a commit that moves the row to another task, or the value the user just cleared
      // would be written straight back instead of failing the required-field validation.
      if (
        isBlank(updateRowWithoutErrors.sampleVolume) &&
        hasTaskLookupChanged(updateRowWithoutErrors, oldRow)
      ) {
        const taskSampleVolume = resolveTaskSampleVolume(updateRowWithoutErrors)
        if (taskSampleVolume !== null) updateRowWithoutErrors.sampleVolume = taskSampleVolume
      }

      const fieldsToCheckForDAP = ['dryWeight', 'gravelWeight', 'sampleVolume', 'gravelVolume']
      const hasRelevantFieldChanged = fieldsToCheckForDAP.some(
        field => updateRowWithoutErrors[field] !== oldRow?.[field],
      )

      const dapFieldsHaveValues = fieldsToCheckForDAP.every(
        field => !isBlank(updateRowWithoutErrors[field]),
      )

      if (hasRelevantFieldChanged || (dapFieldsHaveValues && !oldRow?.dap)) {
        const calculatedDAP = calculateDAP(updateRowWithoutErrors, programConfig)
        if (calculatedDAP !== null) {
          updateRowWithoutErrors.dap = Number(calculatedDAP.toFixed(2))
        }
      }

      const updatedRow = { ...updateRowWithoutErrors, id: newRow.id }

      setMonitoringSOCSitesSamples(oldRows =>
        oldRows.map(row => (row.id === newRow.id ? updatedRow : row)),
      )

      return updatedRow
    },
    [monitoringSOCSitesSamples, programConfig, resolveTaskSampleVolume, hasTaskLookupChanged],
  )

  const isReTerra = programConfig?.programId === PROGRAM_IDS.RETERRA

  const columns = useMemo(() => {
    if (!monitoringSOCSitesColumns || !monitoringSOCSitesSamples) return monitoringSOCSitesColumns

    return monitoringSOCSitesColumns.map(column => {
      if (column.field === 'monitoringSOCActivityId') {
        const allValueOptions: ValueOption[] = Array.isArray(column.valueOptions)
          ? column.valueOptions
          : []

        return {
          ...column,
          valueOptions: (params: { id: string | number }) => {
            const thisRow = monitoringSOCSitesSamples.find(row => row.id === params.id)
            const thisInitialDepth = thisRow?.initialDepth ?? null
            const thisFinalDepth = thisRow?.finalDepth ?? null

            const otherActiveRows = monitoringSOCSitesSamples.filter(
              row => row.id !== params.id && row.monitoringSOCActivityId && !row.isDeleted,
            )

            // Per-activity (not per-program): a double-depth activity (ReTerra or SOC-V2) may
            // appear on a second row as long as no OTHER row already uses it with the exact same
            // (initialDepth, finalDepth). A scalar activity (SOC/POA) may only ever appear once.
            return allValueOptions.filter(option => {
              if (
                typeof option.value === 'string' &&
                doubleDepthActivityIdsRef.current.has(option.value)
              ) {
                return !otherActiveRows.some(
                  row =>
                    row.monitoringSOCActivityId === option.value &&
                    (row.initialDepth ?? null) === thisInitialDepth &&
                    (row.finalDepth ?? null) === thisFinalDepth,
                )
              }
              return !otherActiveRows.some(row => row.monitoringSOCActivityId === option.value)
            })
          },
        }
      }
      return column
    })
  }, [monitoringSOCSitesColumns, monitoringSOCSitesSamples])

  // Returns rows pre-seeded from the double-depth task payload. For ReTerra (all activities
  // double-depth) this always yields at least one row — falling back to one empty placeholder
  // when no payload entries exist yet, since ReTerra mandates depth entry. For mixed programs
  // (SOC-V2 alongside scalar SOC/POA) this yields whatever double-depth rows were pre-loaded
  // from SOC-V2 tasks, or an empty array when none exist — scalar activities are added manually
  // via the grid's "add row" action and are never force-seeded.
  const getInitialSiteRows = useCallback((): SOCSampleRow[] => {
    if (initialSiteRowsRef.current.length > 0) return initialSiteRowsRef.current
    if (isReTerra)
      return [{ id: 'initial-empty-0', isNew: true, initialDepth: null, finalDepth: null }]
    return []
  }, [isReTerra])

  return {
    columns,
    samples: monitoringSOCSitesSamples,
    setSamples: setMonitoringSOCSitesSamples,
    loadColumns,
    processRowUpdate,
    completedSOCActivities,
    getInitialSiteRows,
  }
}
